/**
 * Client-side in-memory cache and request deduplication helper.
 * Prevents redundant refetches and sharing in-flight HTTP promises across components.
 */

const cacheStore = new Map();
const inFlightPromises = new Map();

/**
 * Get cached data or execute fetcherFn with in-flight request deduplication.
 * @param {string} cacheKey - Unique identifier for the cached dataset
 * @param {Function} fetcherFn - Async function returning an Axios response or data Promise
 * @param {number} ttlMs - Time-to-live in milliseconds (default: 30,000ms / 30 seconds)
 * @returns {Promise<any>}
 */
export async function getCachedOrFetch(cacheKey, fetcherFn, ttlMs = 30000) {
  const now = Date.now();

  // 1. Check if valid cached entry exists
  if (cacheStore.has(cacheKey)) {
    const entry = cacheStore.get(cacheKey);
    if (now - entry.timestamp < ttlMs) {
      return entry.data;
    }
    cacheStore.delete(cacheKey);
  }

  // 2. Deduplicate in-flight promises
  if (inFlightPromises.has(cacheKey)) {
    return inFlightPromises.get(cacheKey);
  }

  // 3. Execute fetcher and store promise
  const promise = (async () => {
    try {
      const result = await fetcherFn();
      cacheStore.set(cacheKey, {
        timestamp: Date.now(),
        data: result,
      });
      return result;
    } finally {
      inFlightPromises.delete(cacheKey);
    }
  })();

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

/**
 * Invalidate specific cache keys or patterns.
 * @param {string|RegExp} keyOrPattern
 */
export function invalidateCache(keyOrPattern) {
  if (!keyOrPattern) {
    cacheStore.clear();
    return;
  }

  if (typeof keyOrPattern === 'string') {
    cacheStore.delete(keyOrPattern);
  } else if (keyOrPattern instanceof RegExp) {
    for (const key of cacheStore.keys()) {
      if (keyOrPattern.test(key)) {
        cacheStore.delete(key);
      }
    }
  }
}

/**
 * Clear the entire client cache.
 */
export function clearCache() {
  cacheStore.clear();
  inFlightPromises.clear();
}
